'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { trackEvent, getCommonEventProperties } from '@acko-tech/building-blocks.ui.acko-analytics'
import IllogicalFlowModal from '@components/common/IllogicalFlowModal/illogicalFlowModal'
import { AutoProduct } from 'src/enums/AutoProduct'
import { cleanRegNo, validateRegistrationNumber, formatRegistrationNumber } from '@utils/giCar/registrationValidation'
import { submitAutoProposal } from '@utils/autoProposal/submitAutoProposal'
import { getUrlWithQueryParams } from '@utils/queryParamUtils'
import FormWidget from '@components/GICar/FormWidget'
import { ANALYTICS } from './config'
import { resolveBikeProposalError, resolveBikeProposalResponse, BikeProposalResolution } from './proposalResultResolver'

interface RegistrationNumberProps {
  autoFillNumber?: string
  autoSubmit?: boolean
  formData: Record<string, any>
  headerName?: string
  mobileNumber?: string
  regexPatternsData?: string[]
}

const RegistrationNumber: React.FC<RegistrationNumberProps> = ({
  autoFillNumber = '',
  autoSubmit = false,
  formData,
  headerName = ANALYTICS.HEADER_NAMES,
  mobileNumber = '',
  regexPatternsData = [],
}) => {
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const [localError, setLocalError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [, setProposalAPIResponse] = useState<Record<string, any> | null>(null)
  const [illogicalFlowData, setIllogicalFlowData] = useState<Record<string, any> | null>(null)
  const [showIllogicalFlowModal, setShowIllogicalFlowModal] = useState(false)

  const getCommonEventProps = useCallback(
    (submittedRegistrationNumber: string) => ({
      ...getCommonEventProperties('seo'),
      from_page: typeof document !== 'undefined' ? document.referrer : '',
      header_name: headerName,
      product: ANALYTICS.PRODUCT.AUTO_BIKE,
      funnel: ANALYTICS.FUNNEL.ENTRY,
      registration_number: submittedRegistrationNumber,
    }),
    [headerName]
  )

  const resetIllogicalFlow = useCallback(() => {
    setShowIllogicalFlowModal(false)
    setIllogicalFlowData(null)
    setProposalAPIResponse(null)
  }, [])

  const applyProposalResolution = useCallback((resolution: BikeProposalResolution) => {
    if (resolution.type === 'redirect') {
      setProposalAPIResponse(resolution.proposalData)
      if (typeof window !== 'undefined') {
        window.location.href = getUrlWithQueryParams(resolution.redirectUrl)
      }
      return
    }

    if (resolution.type === 'illogical_flow') {
      setProposalAPIResponse(resolution.proposalData)
      setIllogicalFlowData(resolution.illogicalFlowData)
      setShowIllogicalFlowModal(true)
      return
    }

    if (resolution.type === 'error') {
      setLocalError(resolution.message)
      setIsDirty(true)
      return
    }

    if (resolution.type === 'not_available') {
      if (typeof window !== 'undefined') {
        window.location.href = resolution.redirectUrl
      }
      return
    }

    setProposalAPIResponse(resolution.proposalData)
  }, [])

  const handleSubmit = useCallback(
    (regNumber?: string, source: 'cta' | 'enter' | 'auto' = 'cta') => {
      void (async () => {
        const rawNumber = regNumber || registrationNumber
        const validation = validateRegistrationNumber(rawNumber, regexPatternsData)

        if (!validation.valid) {
          setLocalError(validation.error || '')
          setIsDirty(true)

          if (source === 'cta') {
            try {
              await trackEvent('error_triggered_on_cta', {
                ...getCommonEventProps(rawNumber),
                error_message: validation.error || '',
                context: 'client error',
              })
            } catch (error) {
              console.error('Failed to track error_triggered_on_cta event:', error)
            }
          }

          return
        }

        const normalizedNumber = cleanRegNo(rawNumber)

        if (source === 'cta' || source === 'enter') {
          try {
            await trackEvent('tap_registration_number_cta', getCommonEventProps(rawNumber))
          } catch (error) {
            console.error('Failed to track tap_registration_number_cta event:', error)
          }
        }

        setIsLoading(true)
        setLocalError('')
        resetIllogicalFlow()
        try {
          const proposalResponse = await submitAutoProposal({
            product: AutoProduct.Bike,
            registrationNumber: normalizedNumber,
            mobileNumber,
          })
          applyProposalResolution(resolveBikeProposalResponse(normalizedNumber, proposalResponse))
        } catch (error) {
          console.error('Error submitting bike registration number:', error)
          applyProposalResolution(resolveBikeProposalError(error, normalizedNumber))
        } finally {
          setIsLoading(false)
        }
      })()
    },
    [
      applyProposalResolution,
      getCommonEventProps,
      mobileNumber,
      registrationNumber,
      regexPatternsData,
      resetIllogicalFlow,
    ]
  )

  useEffect(() => {
    if (!autoFillNumber || registrationNumber) return
    const formatted = formatRegistrationNumber(autoFillNumber)
    setRegistrationNumber(formatted)
    const validation = validateRegistrationNumber(formatted, regexPatternsData)
    if (validation.valid && autoSubmit) {
      setTimeout(() => {
        void handleSubmit(formatted, 'auto')
      }, 500)
    }
  }, [autoFillNumber, autoSubmit, handleSubmit, registrationNumber, regexPatternsData])

  const handleChangeHandler = useCallback(
    (value: string) => {
      const formatted = formatRegistrationNumber(value)
      setRegistrationNumber(formatted)
      setLocalError('')
      if (isDirty) {
        const validation = validateRegistrationNumber(formatted, regexPatternsData)
        if (!validation.valid) {
          setLocalError(validation.error || '')
        }
      }
    },
    [isDirty, regexPatternsData]
  )

  const handleBlurHandler = useCallback(() => {
    setIsDirty(true)
    const validation = validateRegistrationNumber(registrationNumber, regexPatternsData)
    setLocalError(validation.valid ? '' : validation.error || '')
  }, [registrationNumber, regexPatternsData])

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && registrationNumber.trim()) {
        e.preventDefault()
        void handleSubmit(undefined, 'enter')
      }
    },
    [handleSubmit, registrationNumber]
  )

  const dynamicFormData = useMemo(
    () => ({
      ...formData,
      ERROR_MESSAGE: localError || formData.ERROR_MESSAGE,
    }),
    [formData, localError]
  )

  return (
    <>
      <FormWidget
        registrationNumber={registrationNumber}
        error={localError}
        isLoading={isLoading}
        onChangeHandler={handleChangeHandler}
        onBlurHandler={handleBlurHandler}
        onClickHandler={() => {
          void handleSubmit(undefined, 'cta')
        }}
        onKeyPressHandler={handleKeyPress}
        formData={dynamicFormData}
      />
      {showIllogicalFlowModal && illogicalFlowData ? (
        <IllogicalFlowModal
          errorCode={illogicalFlowData.error_code}
          illogicalFlowData={illogicalFlowData}
          product={AutoProduct.Bike}
          regNo={illogicalFlowData.registration_number || registrationNumber}
          identifiedVehicleType={illogicalFlowData.vehicle_type || AutoProduct.Car}
          showIllogicalFlowModal={showIllogicalFlowModal}
          setShowIllogicalFlowModal={setShowIllogicalFlowModal}
          onClose={resetIllogicalFlow}
          secondaryCta={resetIllogicalFlow}
          setProposalAPIResponse={setProposalAPIResponse}
        />
      ) : null}
    </>
  )
}

export default RegistrationNumber
